import { Link } from 'react-router-dom';
import { COMPANY } from '../../../mocks/siteData';
import { useEffect, useRef } from 'react';

interface HeroSectionProps {
  heading?: string;
  subheading?: string;
  bgImage?: string;
  primaryCTA?: string;
  primaryCTALink?: string;
  secondaryCTA?: string;
  secondaryCTALink?: string;
  trustLine?: string;
  align?: 'left' | 'center';
}

export default function HeroSection({
  heading = 'Elevated Home Remodeling Designed to Transform How You Live',
  subheading = 'Custom kitchens, bathrooms, and full-home renovations crafted with precision, built for long-term value, and designed to reflect your lifestyle.',
  bgImage = 'https://public.readdy.ai/ai/img_res/d17b2e5b449957ae32ca51066dc89f23.jpg',
  primaryCTA = 'Request a Design Consultation',
  primaryCTALink = '/about',
  secondaryCTA = 'View Our Services',
  secondaryCTALink = '/services/home-remodeling',
  trustLine = 'Licensed · Insured · Local Remodeling Specialists',
  align = 'left',
}: HeroSectionProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = contentRef.current;
    const line = lineRef.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(36px)';
    const t = setTimeout(() => {
      el.style.transition = 'opacity 1.1s cubic-bezier(0.22,1,0.36,1), transform 1.1s cubic-bezier(0.22,1,0.36,1)';
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    }, 120);
    if (line) {
      line.style.height = '0';
      const t2 = setTimeout(() => {
        line.style.transition = 'height 1.2s cubic-bezier(0.22,1,0.36,1)';
        line.style.height = '100%';
      }, 400);
      return () => { clearTimeout(t); clearTimeout(t2); };
    }
    return () => clearTimeout(t);
  }, []);

  const isCenter = align === 'center';

  return (
    <section
      className="relative min-h-screen w-full flex items-end overflow-hidden"
      style={{
        backgroundImage: `url('${bgImage}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center 30%',
      }}
    >
      {/* Multi-layer depth overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/60 to-stone-900/5" />
      <div className="absolute inset-0 bg-gradient-to-r from-stone-950/80 via-stone-950/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-stone-950 to-transparent" />

      {/* Content */}
      <div className="relative z-10 w-full px-6 lg:px-16 xl:px-24 pb-20 pt-36">
        <div className={`${isCenter ? 'max-w-4xl mx-auto text-center' : 'max-w-4xl'}`}>

          {/* Review badge */}
          <div ref={contentRef}>
            <div className={`inline-flex items-center gap-3 mb-9 ${isCenter ? 'justify-center' : ''}`}>
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-star-fill text-accent text-xs" />
                  </div>
                ))}
              </div>
              <div className="w-px h-4 bg-white/20" />
              <span className="text-white/60 text-xs font-medium tracking-wide">{COMPANY.rating} Rating · {COMPANY.reviewCount} Reviews · Brandon, FL</span>
            </div>

            {/* Headline */}
            <div className={`relative ${isCenter ? '' : 'lg:pl-7'}`}>
              {/* Decorative vertical line (desktop only) */}
              {!isCenter && (
                <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-stone-700 overflow-hidden hidden lg:block">
                  <div
                    ref={lineRef}
                    className="w-full bg-gradient-to-b from-accent via-accent/70 to-transparent"
                    style={{ height: '0' }}
                  />
                </div>
              )}
              <h1
                className={`font-serif text-[52px] md:text-[68px] lg:text-[80px] xl:text-[90px] text-white leading-[1.0] tracking-tight mb-7 ${isCenter ? '' : ''}`}
                style={{ letterSpacing: '-0.02em' }}
              >
                {heading}
              </h1>
            </div>

            <p className={`text-white/60 text-base md:text-lg max-w-2xl mb-10 leading-relaxed ${isCenter ? 'mx-auto' : ''}`}>
              {subheading}
            </p>

            {/* CTAs */}
            <div className={`flex flex-col sm:flex-row gap-3 mb-12 ${isCenter ? 'justify-center' : ''}`}>
              <Link
                to={primaryCTALink}
                className="group inline-flex items-center justify-center gap-3 bg-accent hover:bg-accent-dark text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 whitespace-nowrap text-sm tracking-wide"
              >
                {primaryCTA}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm transition-transform duration-200 group-hover:translate-x-1" />
                </div>
              </Link>
              <Link
                to={secondaryCTALink}
                className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/50 bg-white/5 hover:bg-white/10 text-white/90 hover:text-white font-medium px-8 py-4 rounded-lg transition-all duration-300 whitespace-nowrap text-sm tracking-wide backdrop-blur-sm"
              >
                {secondaryCTA}
              </Link>
            </div>

            {/* Bottom trust strip */}
            <div className={`flex flex-wrap items-center gap-x-8 gap-y-3 pt-7 border-t border-white/8 ${isCenter ? 'justify-center' : ''}`}>
              <div className="flex items-center gap-2">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-shield-check-fill text-accent text-xs" />
                </div>
                <span className="text-white/40 text-xs tracking-wide">{trustLine}</span>
              </div>
              <div className="w-px h-3 bg-white/15 hidden sm:block" />
              <a
                href={`tel:${COMPANY.phoneFull}`}
                className="flex items-center gap-2 text-white/50 hover:text-white/80 text-xs transition-colors group"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-phone-fill text-accent text-xs" />
                </div>
                <span className="tracking-wide">{COMPANY.phone}</span>
              </a>
              <div className="w-px h-3 bg-white/15 hidden sm:block" />
              <span className="text-white/30 text-xs tracking-wide">Free Consultation</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 right-10 z-10 hidden lg:flex flex-col items-center gap-3">
        <div className="w-px h-16 bg-gradient-to-b from-white/25 to-transparent" />
        <span className="text-white/25 text-[10px] tracking-[0.2em] uppercase" style={{ writingMode: 'vertical-rl' }}>Scroll</span>
      </div>
    </section>
  );
}
